import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success: false;
  message?: string;
  error?: string;
}

async function parseResponse<T>(res: Response): Promise<T> {
  const json = (await res.json()) as
    | ApiSuccessResponse<T>
    | ApiErrorResponse;
  if (!res.ok) {
    const msg =
      (json as ApiErrorResponse).message ||
      (json as ApiErrorResponse).error ||
      `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return (json as ApiSuccessResponse<T>).data;
}

// ---------------------------------------------------------------------------
// List leads
// ---------------------------------------------------------------------------

export interface AssignmentListItem {
  id: string;
  leadNo: string;
  clientName: string;
  email: string | null;
  phone: string | null;
  status: string;
  stage: string | null;
  markName: string | null;
  createdAt: string;
}

export interface AssignmentListResult {
  items: AssignmentListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type AssignmentStageFilter = "DISCUSSION" | "FILING" | "STATUS";
export type AssignmentSort = "newest" | "oldest";

export interface ListAssignmentLeadsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  stage?: AssignmentStageFilter;
  sort?: AssignmentSort;
}

export async function listAssignmentLeads(
  params: ListAssignmentLeadsParams = {}
): Promise<AssignmentListResult> {
  const q = new URLSearchParams();
  if (params.page != null) q.set("page", String(params.page));
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.search) q.set("search", params.search);
  if (params.status) q.set("status", params.status);
  if (params.stage) q.set("stage", params.stage);
  if (params.sort) q.set("sort", params.sort);

  const res = await apiFetch(`${API_BASE}/assignment/leads?${q.toString()}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  return parseResponse<AssignmentListResult>(res);
}

// ---------------------------------------------------------------------------
// Next lead number
// ---------------------------------------------------------------------------

export async function getAssignmentNextLeadNumber(): Promise<{ leadNo: string }> {
  const res = await apiFetch(
    `${API_BASE}/assignment/discussion/next-lead-number`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadNo: string }>(res);
}

// ---------------------------------------------------------------------------
// Get discussion details (edit mode)
// ---------------------------------------------------------------------------

export interface AssignmentApplicationDetailsView {
  addresseeDesignation: string | null;
  addresseeCustomName: string | null;
  formOfAddress: string | null;
  firmName: string | null;
  place: string | null;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  dateOfPowerOfAttorney: string | null;
  descriptionOfApplicant: string | null;
}

export interface AssignmentApplicationDocumentView {
  id: string;
  fileKey: string;
  fileName: string;
  fileUrl: string | null;
  sortOrder: number;
  applicationOrdinal: number | null;
}

export interface AssignmentApplicationView {
  id: string;
  tmNo: string;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  validityDate: string | null;
  sortOrder: number;
}

export interface AssignmentDiscussionDetails {
  leadId: string;
  leadNo: string;
  module: string;
  stage: string | null;
  initiationType: string | null;
  referenceLeadId: string | null;
  referenceLead: { id: string; leadNo: string; module: string } | null;
  preliminary: {
    markName: string | null;
    contactName: string;
    phone: string | null;
    email: string | null;
    callTime: string | null;
  };
  applicationDetails: AssignmentApplicationDetailsView | null;
  documents: AssignmentApplicationDocumentView[];
  applications: AssignmentApplicationView[];
}

export async function getAssignmentDiscussionDetails(
  leadId: string
): Promise<AssignmentDiscussionDetails> {
  const res = await apiFetch(
    `${API_BASE}/assignment/discussion/${leadId}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentDiscussionDetails>(res);
}

// ---------------------------------------------------------------------------
// Create preliminary discussion
// ---------------------------------------------------------------------------

export type AssignmentInitiationType = "CLIENT_INITIATED" | "EXISTING_LEAD";

export interface CreateAssignmentDiscussionPayload {
  markName: string;
  contactName: string;
  phone: string;
  email?: string;
  callTime: string;
  initiationType: AssignmentInitiationType;
  referenceLeadId?: string;
}

export interface CreateAssignmentDiscussionResponse {
  leadId: string;
  leadNo: string;
  initiationType: AssignmentInitiationType;
  referenceLeadId: string | null;
}

export async function createAssignmentDiscussion(
  payload: CreateAssignmentDiscussionPayload
): Promise<CreateAssignmentDiscussionResponse> {
  const body: Record<string, unknown> = { ...payload };
  if (payload.initiationType === "CLIENT_INITIATED") {
    delete body.referenceLeadId;
  }
  const res = await apiFetch(`${API_BASE}/assignment/discussion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseResponse<CreateAssignmentDiscussionResponse>(res);
}

// ---------------------------------------------------------------------------
// Update preliminary discussion
// ---------------------------------------------------------------------------

export interface UpdateAssignmentPreliminaryPayload {
  markName?: string;
  contactName?: string;
  phone?: string;
  email?: string;
  callTime?: string;
}

export async function updateAssignmentPreliminary(
  leadId: string,
  payload: UpdateAssignmentPreliminaryPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/assignment/discussion/${leadId}/preliminary`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Lead prefill (GET /leads/:id/prefill) — used when selecting an existing
// new-application lead as a reference for an EXISTING_LEAD assignment.
// ---------------------------------------------------------------------------

export interface LeadPrefillResponse {
  referenceLeadId: string;
  mark: string | null;
  classes: number[] | null;
  goodsServicesDescription: string | null;
  imageOfMarkKey: string | null;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  descriptionOfApplicant: string | null;
  dateOfPowerOfAttorney: string | null;
  addressee: {
    addresseeLine1: string | null;
    addresseeLine2: string | null;
    addresseeLine3: string | null;
    districtOfApplicant: string | null;
    formOfAddress: string | null;
  } | null;
  applications: Array<{
    mark: string | null;
    classNumber: number | null;
    typeOfMark: string | null;
    tmNo: string | null;
    applDate: string | null;
    status: string | null;
    userDate: {
      userDateType: "PROPOSED_TO_BE_USED" | "SPECIFIC_DATE" | null;
      date: string | null;
    } | null;
    validityDate: string | null;
  }> | null;
  [key: string]: unknown;
}

export async function getLeadPrefill(leadId: string): Promise<LeadPrefillResponse> {
  const res = await apiFetch(`${API_BASE}/leads/${leadId}/prefill`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  return parseResponse<LeadPrefillResponse>(res);
}

// ---------------------------------------------------------------------------
// Step 2: Application Details from Client — upload + form submission
// ---------------------------------------------------------------------------

export interface AssignmentInvoiceApplicationInput {
  tmNo: string;
  dateOfFiling?: string | null;
  mark?: string | null;
  typeOfMark?: string | null;
  classNumber?: number | null;
  validityDate?: string | null;
}

export interface SubmitAssignmentApplicationDocumentInput {
  fileKey: string;
  fileName: string;
  /** Zero-based index of the TM application this document belongs to (matches `applications[]`). */
  applicationOrdinal?: number;
}

export interface SubmitAssignmentApplicationDetailsPayload {
  documents?: SubmitAssignmentApplicationDocumentInput[];
  addresseeDesignation?: string;
  addresseeCustomName?: string;
  formOfAddress?: string;
  firmName?: string;
  place?: string;
  applicant?: string;
  applicantAddress?: string;
  applicantType?: string;
  signatoryName?: string;
  signatoryDesignation?: string;
  dateOfPowerOfAttorney?: string;
  descriptionOfApplicant?: string;
  applications?: AssignmentInvoiceApplicationInput[];
}

export async function submitAssignmentApplicationDetails(
  leadId: string,
  payload: SubmitAssignmentApplicationDetailsPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/assignment/discussion/${leadId}/application-details`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

export interface AssignmentUploadPresignedUrl {
  key: string;
  bucket: string;
  url: string;
}

export async function generateAssignmentUploadPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<AssignmentUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `assignment/discussion/${leadId}`,
    }),
  });
  return parseResponse<AssignmentUploadPresignedUrl[]>(res);
}

// ---------------------------------------------------------------------------
// Static option lists — reused across assignment discussion steps. Mirrors
// follow-up module options so the two flows stay visually consistent.
// ---------------------------------------------------------------------------

export const ASSIGNMENT_ADDRESSEE_DESIGNATIONS = [
  "The Proprietor",
  "The Managing Partner",
  "The Designated Partner",
  "The Managing Director",
  "The Managing Trustee",
  "The Chairman",
  "NAME",
] as const;

export const ASSIGNMENT_APPLICANT_TYPES = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  { value: "LIMITED_LIABILITY_PARTNERSHIP", label: "Limited Liability Partnership" },
  { value: "OTHERS", label: "Others" },
] as const;

export const ASSIGNMENT_FORM_OF_ADDRESS_OPTIONS = ["Sir", "Madam"] as const;

export const ASSIGNMENT_TYPE_OF_MARK_OPTIONS = ["Word", "Logo", "Label", "Slogan"] as const;

export const ASSIGNMENT_CLASS_NUMBERS = Array.from({ length: 44 }, (_, i) => i + 1);

export const ASSIGNMENT_PLACE_OPTIONS = [
  "Thiruvananthapuram",
  "Kollam",
  "Pathanamthitta",
  "Alappuzha",
  "Kottayam",
  "Idukki",
  "Ernakulam",
  "Thrissur",
  "Palakkad",
  "Malappuram",
  "Kozhikode",
  "Wayanad",
  "Kannur",
  "Kasaragod",
] as const;

// ---------------------------------------------------------------------------
// Step 3: Invoice to Client for Assignment
// ---------------------------------------------------------------------------

export interface AssignmentInvoicePreviewApplication {
  id: string;
  tmNo: string;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  validityDate: string | null;
}

export interface AssignmentInvoicePreview {
  addresseeDesignation: string | null;
  addresseeCustomName: string | null;
  firmName: string | null;
  place: string | null;
  formOfAddress: string | null;
  addressee: string;
  mark: string;
  selectedClass: string;
  selectedTypeOfMark: string;
  noOfApplications: number;
  deedPrepPerApp: number;
  tmpOwnershipPerApp: number;
  tmpAttorneyPerApp: number;
  poaPrepPerApp: number;
  nocPrepPerApp: number;
  affidavitPrepPerApp: number;
  sofPrepPerApp: number;
  totalDeedPrep: number;
  totalTmpOwnership: number;
  totalTmpAttorney: number;
  totalPoaPrep: number;
  totalNocPrep: number;
  totalAffidavitPrep: number;
  totalSofPrep: number;
  totalCharges: number;
  discountProfFeeAssignment: number;
  totalDiscount: number;
  invoiceAmount: number;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNo: boolean;
  collectedAmount: number;
  outstandingAmount: number;
  applications: AssignmentInvoicePreviewApplication[];
  invoiceDocumentUrl: string | null;
  invoiceDocumentFilename: string | null;
}

export async function getAssignmentInvoicePreview(
  leadId: string
): Promise<AssignmentInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/assignment/invoice/${leadId}/to-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentInvoicePreview>(res);
}

export interface SaveAssignmentInvoicePayload {
  invoiceDate?: string;
  discountProfFeeAssignment?: number;
  includeInvoiceNo?: boolean;
  invoiceAmount?: number;
}

export async function saveAssignmentInvoice(
  leadId: string,
  payload: SaveAssignmentInvoicePayload
): Promise<AssignmentInvoicePreview & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/assignment/invoice/${leadId}/to-client/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<AssignmentInvoicePreview & { leadId: string }>(res);
}

export interface AssignmentInvoicePdfResponse {
  s3Key: string;
  downloadUrl: string;
  filename: string;
}

export async function generateAssignmentInvoicePdf(
  leadId: string
): Promise<AssignmentInvoicePdfResponse> {
  const res = await apiFetch(
    `${API_BASE}/assignment/invoice/${leadId}/to-client/generate`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentInvoicePdfResponse>(res);
}

export interface SendAssignmentInvoicePayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
}

export interface SendAssignmentInvoiceResponse {
  whatsAppLink?: string;
  emailSent?: boolean;
  leadId?: string;
}

export async function sendAssignmentInvoiceToClient(
  leadId: string,
  payload: SendAssignmentInvoicePayload = {}
): Promise<SendAssignmentInvoiceResponse> {
  const res = await apiFetch(
    `${API_BASE}/assignment/invoice/${leadId}/to-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<SendAssignmentInvoiceResponse>(res);
}

export interface AssignmentInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface AssignmentInvoicePaymentMessagePreview {
  message: string;
  waLink: string;
  emailPreview: AssignmentInvoiceEmailPreview;
}

export async function getAssignmentInvoicePaymentMessagePreview(
  leadId: string
): Promise<AssignmentInvoicePaymentMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/assignment/invoice/${leadId}/to-client/payment-message-preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentInvoicePaymentMessagePreview>(res);
}

export async function sendAssignmentInvoicePaymentMessage(
  leadId: string
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/assignment/invoice/${leadId}/to-client/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ waLink: string }>(res);
}

// ---------------------------------------------------------------------------
// Step 4: Payment from Client for Assignment
// ---------------------------------------------------------------------------

export interface RecordAssignmentPaymentPayload {
  /** Delta — amount received in this payment, added to any prior payments. */
  collectedAmount: number;
}

export interface AssignmentPaymentRecordResponse {
  invoiceAmount: number;
  collectedAmount: number;
  outstandingAmount: number;
}

export async function recordAssignmentPaymentFromClient(
  leadId: string,
  payload: RecordAssignmentPaymentPayload
): Promise<AssignmentPaymentRecordResponse> {
  const res = await apiFetch(`${API_BASE}/assignment/invoice/${leadId}/payment`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parseResponse<AssignmentPaymentRecordResponse>(res);
}

// ---------------------------------------------------------------------------
// Skip step (Progress button)
// ---------------------------------------------------------------------------

export interface SkipAssignmentStepResponse {
  step: string;
  status: string;
}

export async function skipAssignmentStep(
  leadId: string,
  step: string
): Promise<SkipAssignmentStepResponse> {
  const res = await apiFetch(
    `${API_BASE}/assignment/discussion/${leadId}/workflow/skip-step`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ step }),
    }
  );
  return parseResponse<SkipAssignmentStepResponse>(res);
}

// ---------------------------------------------------------------------------
// Workflow progress — used to detect already-sent steps for resend UX.
// ---------------------------------------------------------------------------

export interface AssignmentProgressStep {
  slug: string;
  label: string;
  stage: string;
  stepType: string;
  parentStep?: string;
  isCompleted: boolean;
  isSkipped: boolean;
}

export interface AssignmentProgressResponse {
  currentStep: string | null;
  currentSection: string | null;
  maxStepReached: string | null;
  steps: AssignmentProgressStep[];
}

export async function getAssignmentProgress(
  leadId: string
): Promise<AssignmentProgressResponse> {
  const res = await apiFetch(
    `${API_BASE}/assignment/discussion/${leadId}/progress`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentProgressResponse>(res);
}

/** Backend AssignmentStep enum slugs (string values) used for workflow progress lookup. */
export const AssignmentWorkflowStep = {
  AS_S1_01_PreliminaryDiscussion: "AS_S1_01_PRELIMINARY_DISCUSSION",
  AS_S1_02_ApplicationDetailsFromClient: "AS_S1_02_APPLICATION_DETAILS_FROM_CLIENT",
  AS_S1_03_InvoiceToClient: "AS_S1_03_INVOICE_TO_CLIENT",
  AS_S1_04_PaymentFromClient: "AS_S1_04_PAYMENT_FROM_CLIENT",
  AS_S2_01_StampPaperDetailsToVendor: "AS_S2_01_STAMP_PAPER_DETAILS_TO_VENDOR",
  AS_S2_02_StampPaperFromVendor: "AS_S2_02_STAMP_PAPER_FROM_VENDOR",
  AS_S2_03_PreparationOfPoA: "AS_S2_03_PREPARATION_OF_POA",
  AS_S2_03a_PoAFieldsSaved: "AS_S2_03A_POA_FIELDS_SAVED",
  AS_S2_03b_PoAPdfGenerated: "AS_S2_03B_POA_PDF_GENERATED",
  AS_S2_04_FileFormsForAssignment: "AS_S2_04_FILE_FORMS_FOR_ASSIGNMENT",
  AS_S2_05_InformClientOfFiling: "AS_S2_05_INFORM_CLIENT_OF_FILING",
  AS_S3_01_StatusDocuments: "AS_S3_01_STATUS_DOCUMENTS",
  AS_S3_02_NoticeToClient: "AS_S3_02_NOTICE_TO_CLIENT",
  AS_S3_03_InformClientAboutAssignment: "AS_S3_03_INFORM_CLIENT_ABOUT_ASSIGNMENT",
} as const;

// ---------------------------------------------------------------------------
// Stage 2, Step 1: Stamp Paper details to Vendor for Assignment
// ---------------------------------------------------------------------------

export type AssignmentStampPaperMessageType =
  | "applicant_poa"
  | "assignee_poa"
  | "assignment_deed"
  | "affidavit";

export interface AssignmentStampPaperMessagePreview {
  type: AssignmentStampPaperMessageType;
  label: string;
  stampCount: number;
  message: string | null;
  waLink: string | null;
}

export interface AssignmentStampPaperApplication {
  id: string;
  tmNo: string;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  validityDate: string | null;
}

export interface AssignmentStampPaperPreviewResponse {
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  assignee: string | null;
  assigneeAddress: string | null;
  assigneeType: string | null;
  assigneeSignatoryName: string | null;
  assigneeSignatoryDesignation: string | null;
  applicantPoaStamps: number;
  assigneePoaStamps: number;
  assignmentDeedStamps: number;
  otherDocumentsStamps: number;
  stampVendorName: string | null;
  stampVendorPhone: string | null;
  calculatedVendorAmount: number;
  vendorAmountPayable: number | null;
  vendorPaid: boolean;
  vendorPaymentStatus: "PAID" | "PENDING";
  messages: AssignmentStampPaperMessagePreview[];
  applications: AssignmentStampPaperApplication[];
  stampVendors: { name: string; phone: string; isDefault?: boolean }[];
}

export interface SaveAssignmentStampPaperPayload {
  assignee?: string;
  assigneeAddress?: string;
  assigneeType?: string;
  assigneeSignatoryName?: string;
  assigneeSignatoryDesignation?: string;
  applicantPoaStamps?: number;
  assigneePoaStamps?: number;
  assignmentDeedStamps?: number;
  otherDocumentsStamps?: number;
  stampVendorName?: string;
  stampVendorPhone?: string;
}

export async function getAssignmentStampPaperPreview(
  leadId: string
): Promise<AssignmentStampPaperPreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/stamp-paper/${leadId}/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentStampPaperPreviewResponse>(res);
}

export async function saveAssignmentStampPaper(
  leadId: string,
  payload: SaveAssignmentStampPaperPayload
): Promise<AssignmentStampPaperPreviewResponse & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/stamp-paper/${leadId}/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<AssignmentStampPaperPreviewResponse & { leadId: string }>(res);
}

export async function prefillAssignmentStampPaperApplicant(
  leadId: string
): Promise<AssignmentStampPaperPreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/stamp-paper/${leadId}/prefill-applicant`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentStampPaperPreviewResponse>(res);
}

export async function sendAssignmentStampPaperWhatsApp(
  leadId: string,
  type: AssignmentStampPaperMessageType
): Promise<{ waLink: string; leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/stamp-paper/${leadId}/send/${type}`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ waLink: string; leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 2: Stamp Paper from Vendor (31r amount + 31s paid)
// ---------------------------------------------------------------------------

export type AssignmentVendorPaymentStatus = "PAID" | "PENDING";

export interface AssignmentStampPaperFromVendorPreview {
  applicantPoaStamps: number;
  assigneePoaStamps: number;
  assignmentDeedStamps: number;
  otherDocumentsStamps: number;
  calculatedVendorAmount: number;
  vendorAmountPayable: number | null;
  effectiveVendorAmount: number;
  vendorPaid: boolean;
  vendorPaymentStatus: AssignmentVendorPaymentStatus;
}

export interface SaveAssignmentStampPaperFromVendorPayload {
  /** Override the auto-calculated amount; null clears the override. Omit to leave alone. */
  vendorAmountPayable?: number | null;
  vendorPaid?: boolean;
}

export async function getAssignmentStampPaperFromVendorPreview(
  leadId: string
): Promise<AssignmentStampPaperFromVendorPreview> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/stamp-paper-from-vendor/${leadId}/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentStampPaperFromVendorPreview>(res);
}

export async function saveAssignmentStampPaperFromVendor(
  leadId: string,
  payload: SaveAssignmentStampPaperFromVendorPayload
): Promise<AssignmentStampPaperFromVendorPreview & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/stamp-paper-from-vendor/${leadId}/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<
    AssignmentStampPaperFromVendorPreview & { leadId: string }
  >(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 3: Preparation of Power of Attorney for Assignment
// ---------------------------------------------------------------------------

export type AssignmentPoaParty = "applicant" | "assignee";

export interface AssignmentPoaPdfMeta {
  key: string;
  url: string;
  filename: string;
}

export interface AssignmentPoaPartyItem {
  name: string | null;
  address: string | null;
  type: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  description: string | null;
  templateVariant: string;
  pdf: AssignmentPoaPdfMeta | null;
  pdfIsStale: boolean;
  pdfGeneratedAt: string | null;
  pdfGeneratedBy: string | null;
}

export interface AssignmentPoaContextResponse {
  dateOfPowerOfAttorney: string | null;
  dateOfPowerOfAttorneyFormatted: string | null;
  mark: string | null;
  applicant: AssignmentPoaPartyItem;
  assignee: AssignmentPoaPartyItem;
}

export interface SaveAssignmentPoaPayload {
  /** 2sa) Date of Power of Attorney (YYYY-MM-DD). Persists into application details. */
  dateOfPowerOfAttorney?: string;
  /** 31t) Description of Assignee. */
  descriptionOfAssignee?: string;
}

export async function getAssignmentPoaContext(
  leadId: string
): Promise<AssignmentPoaContextResponse> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/poa/${leadId}/context`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentPoaContextResponse>(res);
}

export async function saveAssignmentPoa(
  leadId: string,
  payload: SaveAssignmentPoaPayload
): Promise<AssignmentPoaContextResponse> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/poa/${leadId}/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<AssignmentPoaContextResponse>(res);
}

export async function generateAssignmentPoaPdf(
  leadId: string,
  party: AssignmentPoaParty,
  force = false
): Promise<AssignmentPoaPdfMeta> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/poa/${leadId}/generate-pdf/${party}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ force }),
    }
  );
  return parseResponse<AssignmentPoaPdfMeta>(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 4: File Forms for Assignment
// ---------------------------------------------------------------------------

export interface AssignmentFileFormsApplicationItem {
  applicationId: string;
  tmNo: string;
  /** Date of Filing of the original application (read-only). */
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  validityDate: string | null;
  /** 31u) Date of Filing Assignment (YYYY-MM-DD). */
  dateOfFilingAssignment: string | null;
  assignmentFormPdfFileKey: string | null;
  assignmentFormPdfFilename: string | null;
  /** Presigned download URL — null when no PDF has been uploaded yet. */
  pdfDownloadUrl: string | null;
  isPdfUploaded: boolean;
}

export interface AssignmentFileFormsResponse {
  applications: AssignmentFileFormsApplicationItem[];
}

export interface SaveAssignmentFileFormsItem {
  applicationId: string;
  /** YYYY-MM-DD; null clears; omit to leave unchanged. */
  dateOfFilingAssignment?: string | null;
  assignmentFormPdfFileKey?: string | null;
  assignmentFormPdfFilename?: string | null;
}

export interface SaveAssignmentFileFormsPayload {
  applications: SaveAssignmentFileFormsItem[];
}

export async function getAssignmentFileForms(
  leadId: string
): Promise<AssignmentFileFormsResponse> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/${leadId}/file-forms`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentFileFormsResponse>(res);
}

export async function saveAssignmentFileForms(
  leadId: string,
  payload: SaveAssignmentFileFormsPayload
): Promise<AssignmentFileFormsResponse & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/${leadId}/file-forms`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<AssignmentFileFormsResponse & { leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 5: Inform Client about Filing of Assignment
// ---------------------------------------------------------------------------

export interface AssignmentInformClientOfFilingPreview {
  message: string;
  recipientPhone: string;
  applicationNumbers: string;
}

export interface AssignmentInformClientOfFilingSendResult {
  leadId: string;
  waLink: string;
  message: string;
  sentViaApi: boolean;
}

export async function getAssignmentInformClientOfFilingPreview(
  leadId: string
): Promise<AssignmentInformClientOfFilingPreview> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/${leadId}/inform-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentInformClientOfFilingPreview>(res);
}

export async function sendAssignmentInformClientOfFilingWhatsApp(
  leadId: string
): Promise<AssignmentInformClientOfFilingSendResult> {
  const res = await apiFetch(
    `${API_BASE}/assignment/filing/${leadId}/inform-client/whatsapp`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentInformClientOfFilingSendResult>(res);
}

// ---------------------------------------------------------------------------
// Stage 3, Step 1: Status of Assignment — per-application status PDFs
// (Assignment Intimation + Status of Application downloaded from TM Registry).
// ---------------------------------------------------------------------------

export interface AssignmentStatusApplicationItem {
  applicationId: string;
  tmNo: string;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  assignmentIntimationPdfFileKey: string | null;
  assignmentIntimationPdfFilename: string | null;
  assignmentIntimationDownloadUrl: string | null;
  isAssignmentIntimationUploaded: boolean;
  statusOfApplicationPdfFileKey: string | null;
  statusOfApplicationPdfFilename: string | null;
  statusOfApplicationDownloadUrl: string | null;
  isStatusOfApplicationUploaded: boolean;
}

export interface AssignmentStatusResponse {
  applications: AssignmentStatusApplicationItem[];
}

export interface SaveAssignmentStatusItem {
  applicationId: string;
  assignmentIntimationPdfFileKey?: string | null;
  assignmentIntimationPdfFilename?: string | null;
  statusOfApplicationPdfFileKey?: string | null;
  statusOfApplicationPdfFilename?: string | null;
}

export interface SaveAssignmentStatusPayload {
  applications: SaveAssignmentStatusItem[];
}

export async function getAssignmentStatus(
  leadId: string
): Promise<AssignmentStatusResponse> {
  const res = await apiFetch(
    `${API_BASE}/assignment/status/${leadId}/status-documents`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentStatusResponse>(res);
}

export async function saveAssignmentStatus(
  leadId: string,
  payload: SaveAssignmentStatusPayload
): Promise<AssignmentStatusResponse & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/assignment/status/${leadId}/status-documents`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<AssignmentStatusResponse & { leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Stage 3, Step 2: Notice to Client of Assignment — forwards every uploaded
// status PDF (Assignment Intimation + Status of Application) to the client.
// One WhatsApp message + one email per PDF; preview returns one item per PDF.
// ---------------------------------------------------------------------------

export type AssignmentNoticePdfKind =
  | "assignment_intimation"
  | "status_of_application";

export interface AssignmentNoticePdfItem {
  applicationId: string;
  tmNo: string;
  kind: AssignmentNoticePdfKind;
  filename: string;
  previewMessage: string;
}

export interface AssignmentEmailDocumentItem {
  label: string;
  filename: string;
}

export interface AssignmentEmailPreview {
  subject: string;
  documents: AssignmentEmailDocumentItem[];
}

export interface AssignmentNoticeToClientPreview {
  recipientPhone: string | null;
  recipientEmail: string | null;
  totalPdfsToForward: number;
  items: AssignmentNoticePdfItem[];
  emailPreview: AssignmentEmailPreview | null;
}

export interface AssignmentNoticeToClientSendResult {
  leadId: string;
  pdfsForwarded: number;
  waLinks: string[];
  sentViaApi: boolean;
  emailsSent: number;
}

export interface SendAssignmentNoticeToClientPayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
  applicationIds?: string[];
}

export async function getAssignmentNoticeToClientPreview(
  leadId: string,
  applicationIds?: string[]
): Promise<AssignmentNoticeToClientPreview> {
  const q = new URLSearchParams();
  if (applicationIds?.length) {
    for (const id of applicationIds) q.append("applicationIds", id);
  }
  const qs = q.toString();
  const res = await apiFetch(
    `${API_BASE}/assignment/status/${leadId}/notice-to-client/preview${qs ? `?${qs}` : ""}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentNoticeToClientPreview>(res);
}

export async function sendAssignmentNoticeToClient(
  leadId: string,
  payload: SendAssignmentNoticeToClientPayload = {}
): Promise<AssignmentNoticeToClientSendResult> {
  const res = await apiFetch(
    `${API_BASE}/assignment/status/${leadId}/notice-to-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<AssignmentNoticeToClientSendResult>(res);
}

// ---------------------------------------------------------------------------
// Stage 3, Step 3: Inform Client about Assignment — confirms assignment is
// complete via WhatsApp + plain completion email (no PDFs — those were sent in Step 2).
// ---------------------------------------------------------------------------

export interface AssignmentInformClientAboutAssignmentPreview {
  message: string;
  recipientPhone: string | null;
  recipientEmail: string | null;
  applicationNumbers: string;
}

export interface AssignmentInformClientAboutAssignmentSendResult {
  leadId: string;
  waLink: string | null;
  sentViaApi: boolean;
  emailsSent: number;
  message: string;
}

export interface SendAssignmentInformClientAboutAssignmentPayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
  applicationIds?: string[];
}

export async function getAssignmentInformClientAboutAssignmentPreview(
  leadId: string,
  applicationIds?: string[]
): Promise<AssignmentInformClientAboutAssignmentPreview> {
  const q = new URLSearchParams();
  if (applicationIds?.length) {
    for (const id of applicationIds) q.append("applicationIds", id);
  }
  const qs = q.toString();
  const res = await apiFetch(
    `${API_BASE}/assignment/status/${leadId}/inform-client/preview${qs ? `?${qs}` : ""}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<AssignmentInformClientAboutAssignmentPreview>(res);
}

export async function sendAssignmentInformClientAboutAssignment(
  leadId: string,
  payload: SendAssignmentInformClientAboutAssignmentPayload = {}
): Promise<AssignmentInformClientAboutAssignmentSendResult> {
  const res = await apiFetch(
    `${API_BASE}/assignment/status/${leadId}/inform-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<AssignmentInformClientAboutAssignmentSendResult>(res);
}
